import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Plus, ScrollText, LogOut, Globe, Rocket, KeyRound, CalendarDays } from 'lucide-react';
import { AuthContext } from '../App';
import { sa, SaButton, errMsg, Modal } from '../components/superadmin/ui';
import { CompanyTable } from '../components/superadmin/CompanyTable';
import { CompanyFormModal } from '../components/superadmin/CompanyFormModal';
import { CompanyUsersModal } from '../components/superadmin/CompanyUsersModal';
import { ImpersonateModal } from '../components/superadmin/ImpersonateModal';
import { AuditLog } from '../components/superadmin/AuditLog';
import { CompanySetupModal } from '../components/superadmin/CompanySetupModal';
import { OnboardingWizard } from '../components/superadmin/OnboardingWizard';
import { PlatformSettings } from '../components/superadmin/PlatformSettings';
import { SubscriptionModal } from '../components/superadmin/SubscriptionModal';
import { ChangePasswordModal } from '../components/superadmin/ChangePasswordModal';
import { SuperAdminAccounts } from '../components/superadmin/SuperAdminAccounts';
import { WebsitePanel } from '../components/WebsitePanel';
import { DomainsDashboard } from '../components/superadmin/DomainsDashboard';
import { LaunchReadiness } from '../components/superadmin/LaunchReadiness';
import { DemoCalendar } from '../components/superadmin/DemoCalendar';
import { useUnseenLeads, UnseenBadge } from '../components/superadmin/useUnseenLeads';

const TABS = ['companies', 'domains', 'platform', 'demos', 'launch', 'audit'];

export default function SuperAdmin() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const { tab: tabParam } = useParams();
  const tab = TABS.includes(tabParam) ? tabParam : 'companies';
  const setTab = (k) => navigate(k === 'companies' ? '/superadmin' : `/superadmin/${k}`);
  const leads = useUnseenLeads();
  useEffect(() => { if ((tab === 'demos' || tab === 'platform') && leads.unseen > 0) leads.markSeen(); }, [tab, leads.unseen]);
  useEffect(() => { if (typeof Notification !== 'undefined' && Notification.permission === 'default') Notification.requestPermission().catch(() => {}); }, []);
  const [companies, setCompanies] = useState([]);
  const [modal, setModal] = useState(null); // {type, company}
  const [showDeleted, setShowDeleted] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`/superadmin/companies?include_deleted=${showDeleted}`);
      setCompanies(data);
    } catch (e) {
      toast.error(errMsg(e, 'Failed to load companies'));
    }
  }, [showDeleted]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); await load(); } catch (e) { toast.error(errMsg(e, 'Action failed')); }
  };
  const suspend = (c) => window.confirm(`Suspend ${c.name}? All of its users will be locked out immediately.`) && act(() => axios.post(`/superadmin/companies/${c.id}/suspend`), `${c.name} suspended`);
  const reinstate = (c) => act(() => axios.post(`/superadmin/companies/${c.id}/reinstate`), `${c.name} reinstated`);
  const del = (c) => window.confirm(`Delete ${c.name}?\n\nThis is a soft delete: logins are blocked immediately, data is retained. You can purge it permanently later from "Show deleted".`) && act(() => axios.delete(`/superadmin/companies/${c.id}`), `${c.name} deleted (data retained)`);
  const purge = (c) => window.confirm(`PERMANENTLY purge ALL data for ${c.name}? This cannot be undone.`) && act(() => axios.delete(`/superadmin/companies/${c.id}?purge=true`), `${c.name} purged`);

  const stats = {
    total: companies.filter((c) => c.status !== 'deleted').length,
    active: companies.filter((c) => c.status === 'active').length,
    suspended: companies.filter((c) => c.status === 'suspended').length,
    trialing: companies.filter((c) => c.subscription?.status === 'trialing').length,
    proposals: companies.reduce((n, c) => n + (c.counts?.proposals || 0), 0),
  };

  return (
    <div style={sa.page} data-testid="superadmin-page">
      <header style={{ borderBottom: '1px solid #27272A', padding: '16px clamp(16px, 3vw, 40px)', display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center', background: '#0A0A0A', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 40, height: 40, background: '#D90000', display: 'grid', placeItems: 'center' }}><Building2 size={22} /></div>
          <div>
            <p style={{ ...sa.overline, margin: 0 }}>Platform control</p>
            <h1 style={{ ...sa.heading, fontSize: 28, margin: 0, lineHeight: 1 }}>Super Admin</h1>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 13, color: '#A1A1AA' }}>
          <span data-testid="superadmin-email">{user?.email}</span>
          <SaButton variant="ghost" data-testid="superadmin-change-password-btn" onClick={() => setModal({ type: 'password' })}><KeyRound size={14} /> Password</SaButton>
          <SaButton variant="ghost" data-testid="superadmin-logout-btn" onClick={logout}><LogOut size={14} /> Log out</SaButton>
        </div>
      </header>

      <main style={{ padding: 'clamp(16px, 3vw, 40px)', maxWidth: 1400 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16, marginBottom: 36 }}>
          {[['Companies', stats.total], ['Active', stats.active], ['On trial', stats.trialing], ['Suspended', stats.suspended], ['Proposals (all)', stats.proposals]].map(([l, v]) => (
            <div key={l} style={{ ...sa.surface, padding: '20px 24px' }}>
              <p style={{ ...sa.overline, margin: 0 }}>{l}</p>
              <div style={{ ...sa.heading, fontSize: 40, lineHeight: 1.1 }} data-testid={`stat-${l.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{v}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {[['companies', 'Companies', Building2], ['domains', 'Domains', Globe], ['platform', 'Platform', Globe], ['demos', 'Demo calendar', CalendarDays], ['launch', 'Launch', Rocket], ['audit', 'Audit log', ScrollText]].map(([k, l, Icon]) => (
              <button key={k} type="button" data-testid={`tab-${k}`} onClick={() => setTab(k)} style={{
                background: tab === k ? '#141414' : 'transparent', color: tab === k ? '#fff' : '#A1A1AA', border: '1px solid',
                borderColor: tab === k ? '#27272A' : 'transparent', padding: '10px 18px', cursor: 'pointer', fontWeight: 700,
                fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'flex', gap: 8, alignItems: 'center', fontFamily: 'inherit',
              }}><Icon size={14} /> {l}{(k === 'demos' || k === 'platform') && <UnseenBadge count={leads.unseen} testId={`unseen-badge-${k}`} />}</button>
            ))}
          </div>
          {tab === 'companies' && (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <label style={{ fontSize: 12, color: '#A1A1AA', display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                <input type="checkbox" data-testid="show-deleted-toggle" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} /> Show deleted
              </label>
              <SaButton data-testid="new-company-btn" onClick={() => setModal({ type: 'wizard' })}><Plus size={14} /> New print shop</SaButton>
            </div>
          )}
        </div>

        {tab === 'companies' ? (
          <CompanyTable
            companies={companies}
            onEdit={(c) => setModal({ type: 'form', company: c })}
            onSetup={(c) => setModal({ type: 'setup', company: c })}
            onPlan={(c) => setModal({ type: 'plan', company: c })}
            onWebsite={(c) => setModal({ type: 'website', company: c })}
            onUsers={(c) => setModal({ type: 'users', company: c })}
            onImpersonate={(c) => setModal({ type: 'impersonate', company: c })}
            onSuspend={suspend}
            onReinstate={reinstate}
            onDelete={del}
            onPurge={purge}
          />
        ) : tab === 'platform' ? <><SuperAdminAccounts /><PlatformSettings /></> : tab === 'domains' ? <DomainsDashboard /> : tab === 'demos' ? <DemoCalendar /> : tab === 'launch' ? <LaunchReadiness /> : <AuditLog companies={companies} />}
      </main>

      {modal?.type === 'form' && <CompanyFormModal company={modal.company} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal?.type === 'setup' && <CompanySetupModal company={modal.company} onClose={() => setModal(null)} onChanged={load} />}
      {modal?.type === 'plan' && <SubscriptionModal company={modal.company} onClose={() => setModal(null)} onChanged={load} />}
      {modal?.type === 'wizard' && <OnboardingWizard companies={companies} onClose={() => { setModal(null); load(); }} onDone={() => { setModal(null); load(); }} />}
      {modal?.type === 'users' && <CompanyUsersModal company={modal.company} onClose={() => setModal(null)} onChanged={load} />}
      {modal?.type === 'impersonate' && <ImpersonateModal company={modal.company} onClose={() => setModal(null)} />}
      {modal?.type === 'password' && <ChangePasswordModal onClose={() => setModal(null)} />}
      {modal?.type === 'website' && (
        <Modal title={`${modal.company.name} — website`} onClose={() => setModal(null)} width={960} testId="sa-website-modal">
          <WebsitePanel baseUrl={`/superadmin/companies/${modal.company.id}/website`} previewPath={`/superadmin/website-preview?company=${modal.company.id}`} editorPath={`/superadmin/website-editor?company=${modal.company.id}`} />
        </Modal>
      )}
    </div>
  );
}
